import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import {
    Area,
    AreaChart,
    CartesianGrid,
    XAxis,
    YAxis
} from "recharts";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from "../../../../../../../components/ui/card";
import {
    type ChartConfig,
    ChartContainer,
    ChartTooltip,
    ChartTooltipContent,
} from "../../../../../../../components/ui/chart";
import { useFilters } from "../../../../../../../utils/hooks/useFilters";
import { cn } from "../../../../../../../lib/utils";

const TABS = ["forecast", "actual", "comparison"] as const;
type Tab = typeof TABS[number];

function ForecastingChart() {
    const { t } = useTranslation();
    const [{ mode, from, to }] = useFilters();
    const [activeTab, setActiveTab] = useState<Tab>("forecast");

    const chartConfig = useMemo(() => ({
        forecast: {
            label: t("forecast"),
            color: "var(--color-primary)",
        },
    } satisfies ChartConfig), [t]);

    const chartData = useMemo(() => {
        const data = [];
        const start = moment(from).startOf('day');
        const end = moment(to).endOf('day');

        if (mode === "day") {
            for (let hour = 0; hour <= 23; hour++) {
                const timeLabel = moment().hour(hour).format("h A");
                data.push({
                    label: timeLabel,
                    forecast: Math.floor(Math.random() * (70 - 10 + 1)) + 10, // Mock data logic
                });
            }
        } else {
            let current = start.clone();
            while (current.isSameOrBefore(end)) {
                data.push({
                    label: current.format("MMM D"),
                    forecast: Math.floor(Math.random() * (500 - 100 + 1)) + 100,
                });
                current.add(1, 'day');
            }
        }
        return data;
    }, [mode, from, to]);

    return (
        <Card className="tw-w-full tw-h-full tw-rounded-xl tw-border-gray-200">
            <CardHeader className="tw-flex tw-flex-col sm:tw-flex-row tw-items-start sm:tw-items-center tw-justify-between tw-space-y-3 sm:tw-space-y-0 tw-pb-4">
                <div className="tw-space-y-1">
                    <CardTitle className="tw-text-sm tw-font-semibold tw-text-gray-700">
                        {t("forecasting")}
                    </CardTitle>
                    <div className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-muted-foreground">
                        <div className="tw-h-2 tw-w-2 tw-rounded-full tw-bg-primary" />
                        <span>{t("check-ins-avg")}: 7182</span>
                    </div>
                </div>
                <span className="tw-text-xs tw-font-medium tw-text-muted-foreground">
                    {mode === "day" ? moment(from).format("ddd D MMM") : moment(from).format("MMMM")}
                </span>
            </CardHeader>

            <CardContent>
                <div className="tw-flex tw-border-b tw-border-gray-200 tw-mb-6">
                    {TABS.map(tab => (
                        <button
                            key={tab}
                            type="button"
                            onClick={() => setActiveTab(tab)}
                            className={cn(
                                "tw-px-4 tw-py-2 tw-cursor-pointer tw-text-xs tw-font-medium -tw-mb-px tw-border-b-2 tw-transition-colors",
                                activeTab === tab
                                    ? "tw-border-primary tw-text-primary"
                                    : "tw-border-transparent tw-text-gray-500 hover:tw-text-gray-700"
                            )}
                        >
                            {t(tab)}
                        </button>
                    ))}
                </div>

                <ChartContainer config={chartConfig} className="tw-h-[16.25rem] tw-w-full">
                    <AreaChart
                        data={chartData}
                        margin={{ left: -15, right: 10, top: 10, bottom: 0 }}
                    >
                        <defs>
                            <linearGradient id="fillForecast" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="var(--color-forecast)" stopOpacity={0.15} />
                                <stop offset="95%" stopColor="var(--color-forecast)" stopOpacity={0.01} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid vertical={false} strokeDasharray="3 3" className="tw-stroke-muted/40" />
                        <XAxis
                            dataKey="label"
                            tickLine={false}
                            axisLine={false}
                            tickMargin={12}
                            fontSize={11}
                            className="tw-text-muted-foreground"
                        />
                        <YAxis
                            tickLine={false}
                            axisLine={false}
                            fontSize={11}
                            tickMargin={12}
                            className="tw-text-muted-foreground"
                        />
                        <ChartTooltip
                            cursor={{ stroke: 'hsl(var(--muted-foreground))', strokeWidth: 1, strokeDasharray: '4 4' }}
                            content={<ChartTooltipContent indicator="line" />}
                        />
                        <Area
                            dataKey="forecast"
                            type="monotone"
                            stroke="var(--color-forecast)"
                            strokeWidth={2}
                            fill="url(#fillForecast)"
                            dot={false}
                            activeDot={{ r: 4, fill: "var(--color-forecast)", stroke: "#fff", strokeWidth: 2 }}
                        />
                    </AreaChart>
                </ChartContainer>
            </CardContent>
        </Card>
    );
}

export default ForecastingChart;
